import {
  type Announcements,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  type BoardLane,
  type Config,
  OTHER_LANE_LABEL,
  type PeopleMap,
  type QueueTicket,
  type Status,
} from '@hrms/shared'
import { type KeyboardEvent, type MouseEventHandler, useEffect, useMemo, useState } from 'react'
import { Lozenge } from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import { StatusLozenge } from '../tickets/ui.tsx'
import { BoardCard } from './BoardCard.tsx'

type Lane = BoardLane<QueueTicket>

const laneName = (lane: Lane) => (lane.kind === 'status' ? lane.status.name : OTHER_LANE_LABEL)

/** Pointer inside a column wins; otherwise the column the dragged card overlaps most. */
const collision: CollisionDetection = (args) => {
  const hits = pointerWithin(args)
  return hits.length > 0 ? hits : rectIntersection(args)
}

/**
 * Keyboard moves don't use dnd-kit's keyboard sensor (its geometry fights the horizontally
 * scrolling board): Shift + Left/Right moves the focused card to the neighbouring column.
 */
const INSTRUCTIONS =
  'Press Enter to open the ticket. Press Shift and the left or right arrow key to move it to the previous or next column.'

function LaneHead({ lane }: { lane: Lane }) {
  return (
    <div className="flex items-center gap-2 px-1 pt-0.5 pb-2">
      {lane.kind === 'status' ? (
        <StatusLozenge statusId={lane.status.id} statuses={[lane.status]} />
      ) : (
        <span
          className="inline-flex"
          {...hint('Tickets whose status was archived in Settings. Drag one out to move it on.')}
        >
          <Lozenge color="slate">{OTHER_LANE_LABEL}</Lozenge>
        </span>
      )}
      <span className="tabular text-[12.5px] text-muted">
        {lane.total}
        <span className="sr-only"> {lane.total === 1 ? 'ticket' : 'tickets'}</span>
      </span>
    </div>
  )
}

function LaneBody({
  lane,
  children,
  dropRef,
  isOver,
}: {
  lane: Lane
  children: React.ReactNode
  dropRef?: (el: HTMLElement | null) => void
  isOver?: boolean
}) {
  const truncated = lane.total > lane.tickets.length
  return (
    <section
      ref={dropRef}
      aria-label={`${laneName(lane)}: ${lane.total} ${lane.total === 1 ? 'ticket' : 'tickets'}`}
      className={cn(
        'flex min-h-60 flex-col rounded-panel border border-transparent bg-surface-2 p-2 transition-colors',
        isOver && 'border-accent bg-accent-soft',
      )}
    >
      <LaneHead lane={lane} />
      <div className="flex flex-col gap-2">{children}</div>
      {lane.tickets.length === 0 && (
        <p className="px-1 py-3 text-center text-[12.5px] text-faint">No tickets</p>
      )}
      {truncated && (
        <p className="mt-2 px-1 text-[12.5px] text-muted">
          Showing {lane.tickets.length} of {lane.total}. Refine the filters to see the rest.
        </p>
      )}
    </section>
  )
}

function DroppableLane({ lane, children }: { lane: Lane; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: lane.id,
    disabled: lane.kind === 'other',
    data: { lane },
  })
  return (
    <LaneBody lane={lane} dropRef={setNodeRef} isOver={isOver}>
      {children}
    </LaneBody>
  )
}

const openOnEnter = (onOpen: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    onOpen()
  }
}

interface CardProps {
  ticket: QueueTicket
  people: PeopleMap
  config: Config
  now: number
  onOpen: (key: string) => void
}

function StaticCard({ ticket, onOpen, ...rest }: CardProps) {
  return (
    <BoardCard
      {...rest}
      ticket={ticket}
      role="button"
      tabIndex={0}
      aria-label={`${ticket.key}: ${ticket.title}${ticket.confidential ? ' (confidential)' : ''}`}
      onClick={() => onOpen(ticket.key)}
      onKeyDown={openOnEnter(() => onOpen(ticket.key))}
    />
  )
}

function DraggableCard({
  ticket,
  onOpen,
  onStep,
  ...rest
}: CardProps & { onStep: (ticket: QueueTicket, dir: -1 | 1) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ticket.id,
    data: { ticket },
  })
  return (
    <BoardCard
      {...rest}
      {...attributes}
      ref={setNodeRef}
      ticket={ticket}
      draggable
      ghost={isDragging}
      data-ticket-key={ticket.key}
      aria-label={`${ticket.key}: ${ticket.title}${ticket.confidential ? ' (confidential)' : ''}`}
      onMouseDown={listeners?.onMouseDown as MouseEventHandler<HTMLDivElement> | undefined}
      onClick={() => onOpen(ticket.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') return openOnEnter(() => onOpen(ticket.key))(e)
        if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          e.preventDefault()
          onStep(ticket, e.key === 'ArrowRight' ? 1 : -1)
        }
      }}
    />
  )
}

const gridClass =
  'relative grid auto-cols-[minmax(260px,1fr)] grid-flow-col gap-3 overflow-x-auto pb-2 [scrollbar-gutter:stable]'

/**
 * Columns per status (DESIGN.md §3.2). With `onMove` (HR on a mouse/trackpad device) cards can
 * be dragged between columns, or moved with Shift + Left/Right; otherwise they're just clickable.
 */
export function BoardLanes({
  lanes,
  people,
  config,
  now,
  onOpen,
  onMove,
}: {
  lanes: Lane[]
  people: PeopleMap
  config: Config
  now: number
  onOpen: (key: string) => void
  onMove?: (ticket: QueueTicket, to: Status) => void
}) {
  const [active, setActive] = useState<QueueTicket | null>(null)
  // Mouse/trackpad only: touch devices change status in the drawer (PRD).
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }))

  const laneOf = useMemo(() => {
    const m = new Map<string, Lane>()
    for (const l of lanes) m.set(l.id, l)
    return m
  }, [lanes])

  const announcements = useMemo<Announcements>(() => {
    const key = (id: string | number) => {
      for (const l of lanes) for (const t of l.tickets) if (t.id === id) return t.key
      return 'Ticket'
    }
    const name = (id: string | number | undefined) => {
      const l = id === undefined ? undefined : laneOf.get(String(id))
      return l ? laneName(l) : null
    }
    return {
      onDragStart: ({ active }) => `Picked up ${key(active.id)}.`,
      onDragOver: ({ active, over }) =>
        over
          ? `${key(active.id)} is over ${name(over.id)}.`
          : `${key(active.id)} is not over a column.`,
      onDragEnd: ({ active, over }) =>
        over ? `${key(active.id)} dropped on ${name(over.id)}.` : `${key(active.id)} dropped.`,
      onDragCancel: ({ active }) => `Moving ${key(active.id)} was cancelled.`,
    }
  }, [lanes, laneOf])

  const cardProps = { people, config, now, onOpen }

  // Keyboard moves: once the card re-renders in its new column, move focus to it.
  const [focusKey, setFocusKey] = useState<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the lanes re-render
  useEffect(() => {
    if (!focusKey) return
    const el = document.querySelector<HTMLElement>(`[data-ticket-key="${CSS.escape(focusKey)}"]`)
    if (el && el !== document.activeElement) {
      el.focus()
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      setFocusKey(null)
    }
    const t = window.setTimeout(() => setFocusKey(null), 3000)
    return () => window.clearTimeout(t)
  }, [focusKey, lanes])
  const onStep = (ticket: QueueTicket, dir: -1 | 1) => {
    const statusLanes = lanes.filter((l) => l.kind === 'status')
    const i = statusLanes.findIndex((l) => l.id === ticket.statusId)
    // From the Other column, Left goes to the last status column.
    const target = i < 0 ? (dir < 0 ? statusLanes.at(-1) : undefined) : statusLanes[i + dir]
    if (target?.kind !== 'status' || !onMove) return
    setFocusKey(ticket.key)
    onMove(ticket, target.status)
  }

  if (!onMove)
    return (
      <div className={gridClass} data-tour="board-lanes">
        {lanes.map((lane) => (
          <LaneBody key={lane.id} lane={lane}>
            {lane.tickets.map((t) => (
              <StaticCard key={t.id} ticket={t} {...cardProps} />
            ))}
          </LaneBody>
        ))}
      </div>
    )

  const onDragStart = (e: DragStartEvent) => {
    setActive((e.active.data.current?.ticket as QueueTicket | undefined) ?? null)
  }
  const onDragEnd = (e: DragEndEvent) => {
    setActive(null)
    const ticket = e.active.data.current?.ticket as QueueTicket | undefined
    const lane = e.over ? laneOf.get(String(e.over.id)) : undefined
    if (!ticket || !lane || lane.kind !== 'status' || lane.status.id === ticket.statusId) return
    onMove(ticket, lane.status)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActive(null)}
      accessibility={{ announcements, screenReaderInstructions: { draggable: INSTRUCTIONS } }}
    >
      <div className={gridClass} data-tour="board-lanes">
        {lanes.map((lane) => (
          <DroppableLane key={lane.id} lane={lane}>
            {lane.tickets.map((t) => (
              <DraggableCard key={t.id} ticket={t} onStep={onStep} {...cardProps} />
            ))}
          </DroppableLane>
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {active && (
          <BoardCard ticket={active} overlay draggable people={people} config={config} now={now} />
        )}
      </DragOverlay>
    </DndContext>
  )
}
